import React, { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { DataSearchResponseItem, DataSearchResponseItemVersion } from '../../../services/endpoints/data'
import './DataViewCell.css'
import ExtractionNotesDialog from '../Modals/ExtractionNotesDialog/ExtractionNotesDialog'
import ScriptDialog from '../Modals/ScriptsDialog/ScriptsDialog'
import DataSpyFileSelectDialog from '../Modals/DataSpyFileSelectDialog/DataSpyFileSelectDialog'
import TextDialog from '../Modals/TextDialog/TextDialog'
import HtmlDialog from '../Modals/HtmlDialog/HtmlDialog'
import MediaDialog from '../Modals/MediaDialog/MediaDialog'
import LocationMapWrapper from '../Modals/LocationsModal/LocationsWrapper'

type _PortalTooltipPlacement = 'top' | 'bottom'
interface _PortalTooltipState {
    left: number
    top: number
    placement: _PortalTooltipPlacement
}

interface _PortalTooltipProps {
    title?: string
    body: string
    children: React.ReactNode
}

const _PortalTooltip: React.FC<_PortalTooltipProps> = ({ title, body, children }) => {
    const triggerRef = useRef<HTMLSpanElement | null>(null)
    const [state, setState] = useState<_PortalTooltipState | null>(null)

    const updatePosition = useCallback(() => {
        if (!triggerRef.current) return

        const rect = triggerRef.current.getBoundingClientRect()
        const availableTop = rect.top
        const availableBottom = window.innerHeight - rect.bottom
        const placement: _PortalTooltipPlacement =
            availableTop > 140 || availableTop > availableBottom ? 'top' : 'bottom'

        setState({
            left: rect.left + rect.width / 2,
            top: placement === 'top' ? rect.top - 8 : rect.bottom + 8,
            placement,
        })
    }, [])

    const open = useCallback(() => {
        updatePosition()
    }, [updatePosition])

    const close = useCallback(() => {
        setState(null)
    }, [])

    useEffect(() => {
        if (!state) return

        const handle = () => updatePosition()
        window.addEventListener('scroll', handle, true)
        window.addEventListener('resize', handle)

        return () => {
            window.removeEventListener('scroll', handle, true)
            window.removeEventListener('resize', handle)
        }
    }, [state, updatePosition])

    return (
        <>
            <span
                ref={triggerRef}
                className="rgvc-tooltip-trigger"
                onMouseEnter={open}
                onMouseLeave={close}
                onFocusCapture={open}
                onBlurCapture={close}
            >
                {children}
            </span>
            {state &&
                createPortal(
                    <div
                        className={`custom-tooltip rgvc-portal-tooltip rgvc-portal-tooltip--${state.placement}`}
                        role="tooltip"
                        style={{ left: state.left, top: state.top }}
                    >
                        {title && <span className="tooltip-title">{title}</span>}
                        <span className="tooltip-body">{body}</span>
                    </div>,
                    document.body
                )}
        </>
    )
}

type ResultsGridViewColProps =
    | {
            item: DataSearchResponseItem
        }
    | {
            // DevExtreme templates may pass row data under different prop names
            key?: DataSearchResponseItem
            data?: DataSearchResponseItem
        }

export const ResultsGridViewCol: React.FC<ResultsGridViewColProps> = (props) => {
        const item =
            'item' in props ? props.item : props.key ?? props.data

        if(!item) {
            return <></>
        }

        const normalizeExt = (ext: string) => ext.replace(/^\./, '').trim().toLowerCase()

        const isLocalVersion = (v: DataSearchResponseItemVersion) => !v.archived || !!v.checkedOut

        const dataSpyExts = new Set((item.dataSpy ?? []).map(normalizeExt))
        const viewableExts = new Set((item.viewable ?? []).map(normalizeExt))

        const dsItems = item.versions
            .filter(i => dataSpyExts.has(normalizeExt(i.extension)) && isLocalVersion(i))

        const vItems = item.versions
            .filter(i => viewableExts.has(normalizeExt(i.extension)) && isLocalVersion(i))
        .reduce<{[k: string]: DataSearchResponseItemVersion}>((a, b) => {
            a[b.extension] = b

            return a
        }, {})
        
    const dataSpyEnabled = true //todo need to pull settings to see if it's available
    
        return (
            <div className="view-btns d-flex align-items-center flex-wrap">
                <div className="mr-1">
                    <_AdditionalItemsDropdown
                        uploadId={item.uploadId}
                        script={item.script}
                        hasNotes={item.extractionNotesTxt}
                        vehicleName={item.vehicle.displayName}
                        request={{
                            vehicleId: item.vehicle.id,
                            startDate: item.startDate,
                            endDate: item.endDate
                        }}
                    />
                </div>

                {dataSpyEnabled && dsItems.length > 0 && (
                    <div className="mr-1">
                        <_DataSpyViewer items={dsItems} />
                    </div>
                )}

                <_AllViewer fileName={item.displayName} items={vItems} fileId={item.id} />
            </div>
        )
}

interface _ItemsDropdownPropsItem {
    title: string
    iconClassName?: string
    onClick: (e: React.MouseEvent<HTMLElement>) => void
}
interface _ItemsDropdownProps {
    title: string
    items: _ItemsDropdownPropsItem[]
    className: string
}
export const _ItemsDropdown: React.FC<_ItemsDropdownProps> = ({
    title,
    items,
    className
}) => {
     const [open, setOpen] = useState(false)
    const dropdownRef = useRef<HTMLDivElement | null>(null)

    const handleClickOutside = (e: Event) => {
        if (
            dropdownRef.current &&
            e.target instanceof Node &&
            !dropdownRef.current.contains(e.target)
        ) {
            setOpen(false)
        }
    }

    //SEV - Not sure I'm happy with this
    useEffect(() => {
        if (open) {
            document.addEventListener('mousedown', handleClickOutside)
        } else {
            document.removeEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [open])

    const handleOnItemClick = (e: MouseEvent<HTMLLIElement>, cb: (e: MouseEvent<HTMLLIElement>) => void) => {
        setOpen(false)
        cb(e)
    }


    return (
         <>
            <div className="custom-dropdown" ref={dropdownRef}>
                <_PortalTooltip  body={title}>
                    <button
                        type="button"
                        className="rgvc-icon-btn dropdown-trigger"
                        onClick={() => setOpen(!open)}
                        aria-label={title}
                        aria-haspopup="menu"
                        aria-expanded={open}
                    >
                        <span className={`glyphicons ${className}`} aria-hidden="true" />
                        <i className="dx-icon dx-icon-spindown" aria-hidden="true" />
                    </button>
                </_PortalTooltip>
                <ul className={`dropdown-list ${open ? 'active' : ''}`}>
                  {items.map((i, index)=> {
                    return (
                      <li
                        key={`${i.title}-${index}`}
                        className="dropdown-item"
                        onClick={(e) => handleOnItemClick(e, i.onClick)}
                      >
                        <span className={`glyphicons ${i.iconClassName}`} aria-hidden="true" />
                        <span className="dropdown-text">{i.title}</span>
                      </li>
                    ) 
                  })}
                </ul>
            </div>
        </>

    )
}
  
type _AdditionalItemsDropdownDialogs = 'extractionNotes' | 'locations' | 'script'
interface _AdditionalItemsDropdownProps {
    uploadId: number
    script: {id: number, name: string} | null
    hasNotes: boolean
    vehicleName: string
    request: {
        vehicleId: number
        startDate: string
        endDate: string
    }
}

export const _AdditionalItemsDropdown: React.FC<_AdditionalItemsDropdownProps> = ({
    uploadId,
    script,
    hasNotes,
    vehicleName,
    request
}) => {
    const [openDialog, setOpenDialog] = useState<_AdditionalItemsDropdownDialogs | null>(null) 

    const handleCloseDialog = useCallback(() => {
        setOpenDialog(null)
    }, [openDialog])

    const handleItemOnClick = useCallback((type: _AdditionalItemsDropdownDialogs) => {
        return (_) => {
            setOpenDialog(type)
        }
    }, [setOpenDialog])

    const items: _ItemsDropdownPropsItem[] = []
    if(hasNotes && script) items.push({
        title: 'Extraction Notes',
                iconClassName: 'glyphicons-notes',
        onClick: handleItemOnClick('extractionNotes')
    })
    if(script) items.push({
        title: `Script: ${script.name}`,
                iconClassName: 'glyphicons-file',
        onClick: handleItemOnClick('script')
    })
    items.push({
      title: 'Locations',
            iconClassName: 'glyphicons-map-marker',
      onClick: handleItemOnClick('locations')
    })

    if(items.length < 1) {
        return <></>
    }


    return (
      <>
        {openDialog === 'extractionNotes' && (
          <ExtractionNotesDialog
            onClose={handleCloseDialog}
            uploadId={uploadId}
            script={script!}
          />
        )}
        {openDialog === 'script' && (
          <ScriptDialog onClose={handleCloseDialog} script={script!} />
        )}
        {openDialog === 'locations' && (
          <LocationMapWrapper
            isOpen={true}
            onClose={handleCloseDialog}
            vehicleName={vehicleName}
            request={{
              ...request,
              sort: 'gpsTimestamp',
              order: 'asc',
              offset: 0,
              max: 500
            }}
          />
        )}
        <_ItemsDropdown
                    title="Associated Items"
                    items={items}
                    className="glyphicons-package"
        />
      </>
    )
}

interface _DataSpyViewerProps {
    items: DataSearchResponseItemVersion[]
}
export const _DataSpyViewer: React.FC<_DataSpyViewerProps> = ({items}) => {
    const [showDialog, setShowDialog] = useState(false)

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        if(e.altKey) {
            setShowDialog(true)
            return
        }

        window.open(
            `/dataspy/?fileId=${items[0].id}`,
            '_blank',
            'noopener,noreferrer'
        )
    }

    return (
        <>
            {showDialog && <DataSpyFileSelectDialog onClose={() => setShowDialog(false)} versions={items} />}
            <_PortalTooltip
                title="View with DataSpy"
                body="Click to open. Alt-click for options."
            >
                <button
                    type="button"
                    className="rgvc-icon-btn"
                    onClick={handleClick}
                    aria-label="DataSpy Viewer"
                >
                    <span className="glyphicons glyphicons-stats" aria-hidden="true" />
                </button>
            </_PortalTooltip>
        </>
    )
}


type _AllViewerDialogs = 'txt' | 'mp3' | 'mp4' | 'html' | 'htm' | 'xml' | 'dm1'
type _AllViewerButtons = {type: string, glyph: string, name: string, link?: string, dialog?: _AllViewerDialogs}
interface _AllViewerProps {
    items: { [key: string]: DataSearchResponseItemVersion }
    fileId: number
    fileName: string
}
export const _AllViewer: React.FC<_AllViewerProps> = ({
    items, fileId, fileName
}) => {
    const [openDialog, setOpenDialog] = useState<_AllViewerDialogs | null>(null)

    const viewButtons: _AllViewerButtons[] = [
        {type: 'Log CSV', glyph: 'table', name: 'Log CSV', link: '/ui/#!/data/quickview/csv/'},
        {type: 'db', glyph: 'database-search', name: 'DB Viewer', link: '/ui/#!/data/quickview/db/'},
        {type: 'htm', glyph: 'global', name: 'HTML Viewer', dialog: 'htm'},
        {type: 'html', glyph: 'global', name: 'HTML Viewer', dialog: 'html'},
        {type: 'mp3', glyph: 'voice', name: 'MP3 Viewer', dialog: 'mp3'},
        {type: 'mp4', glyph: 'facetime-video', name: 'MP4 Viewer', dialog: 'mp4'},
        {type: 'txt', glyph: 'article', name: 'Text Viewer', dialog: 'txt'},
        {type: 'xml', glyph: 'embed', name: 'XML DTC Viewer', link: '/ui/#!/data/quickview/xml/'},
        {type: 'dm1', glyph: 'embed', name: 'XML DM1 Viewer', link: '/ui/#!/data/quickview/dm1/'}
    ]

    const handleClose = () => setOpenDialog(null)

    const getOnclick = (link: string | undefined, dialog: _AllViewerDialogs | undefined) => {
        if(dialog) {
            return (_) => setOpenDialog(dialog)
        } else if(link) {
            return (_) => {
                window.open(link, '_blank', 'noopener,noreferrer');
            }
        } else {
            throw new Error('No valid option handling viewer button click')
        }

    }

    const buttonList = useMemo(() => {
        return viewButtons
        .filter(i => items[i.type] ? true : false)
        .map<_ItemsDropdownPropsItem>(i => {
            const link = (i.link) ? `${i.link}${fileId}` : undefined
            return {
                title: i.name,
                iconClassName: `glyphicons-${i.glyph}`,
                onClick: getOnclick(link, i.dialog)
            }
        })
    }, [items])

    return (
        <>
            {openDialog ==='htm' && <HtmlDialog fileName={fileName} uploadFileVersionId={items['htm'].id} onClose={handleClose} />}
            {openDialog ==='html' && <HtmlDialog fileName={fileName} uploadFileVersionId={items['html'].id} onClose={handleClose} />}
            {openDialog === 'mp3' && <MediaDialog fileName={fileName} uploadFileVersionId={items['mp3'].id} onClose={handleClose} type='mp3' />}
            {openDialog === 'mp4' && <MediaDialog fileName={fileName} uploadFileVersionId={items['mp4'].id} onClose={handleClose} type='mp4' />}
            {openDialog === 'txt' && <TextDialog fileName={fileName} uploadFileVersionId={items['txt'].id} onClose={handleClose} />}

            {buttonList.length === 1 && (
                <_PortalTooltip
                    title={buttonList[0].tooltipTitle}
                    body={buttonList[0].tooltipBody ?? buttonList[0].title}
                >
                    <button
                        type="button"
                        className="rgvc-icon-btn"
                        onClick={buttonList[0].onClick}
                        aria-label={buttonList[0].title}
                    >
                        <span
                            className={`glyphicons ${buttonList[0].iconClassName ?? ''}`}
                            aria-hidden="true"
                        />
                    </button>
                </_PortalTooltip>
            )}

            {buttonList.length > 1 && (
                <_ItemsDropdown
                    title="Viewers"
                    items={buttonList}
                    className="glyphicons-more-windows"
                />
            )}
        </>
    )
}
